import type { UserPlan } from "./plans";

/**
 * Статус підписки Creem станом на останній вебхук (user_profiles.subscription_status).
 * NULL = legacy-рядок без жодного вебхука після міграції — поводимось як раніше.
 */
export type SubscriptionStatus =
  | "active"
  | "trialing"
  | "past_due"
  | "scheduled_cancel"
  | "canceled"
  | "paused";

const SUBSCRIPTION_STATUSES: ReadonlySet<string> = new Set([
  "active",
  "trialing",
  "past_due",
  "scheduled_cancel",
  "canceled",
  "paused",
]);

export function isSubscriptionStatus(v: unknown): v is SubscriptionStatus {
  return typeof v === "string" && SUBSCRIPTION_STATUSES.has(v);
}

/** Рядок user_profiles у частині, потрібній для деривації плану. */
export type ProfileSubscriptionRow = {
  plan: string | null;
  plan_expires_at: string | null;
  subscription_status: string | null;
};

export type EffectivePlanState = {
  /** Ефективний план після врахування expiry + grace. */
  plan: Exclude<UserPlan, "anonymous">;
  /**
   * СИРА дата закінчення (не nullиться після прострочення) — живить
   * "Pro just ended" notice і чесні лейбли в PricingModal.
   */
  planExpiresAt: string | null;
  subscriptionStatus: SubscriptionStatus | null;
  /** Платний план у БД, але прострочився повз grace → читається як free. */
  isLapsedPro: boolean;
};

const PAID_PLANS: ReadonlySet<string> = new Set(["pro", "studio"]);

/**
 * Grace для активної підписки: charge на renewal і вебхук subscription.paid
 * прилітають ПІСЛЯ моменту plan_expires_at — без вікна чесний Pro-юзер
 * флікає у free на хвилини/години кожен renewal.
 */
const ACTIVE_RENEWAL_GRACE_MS = 24 * 60 * 60 * 1000;
/**
 * Grace для past_due: Creem ретраїть платіж кілька днів (точний розклад не
 * документований). Тримаємо Pro обмежене вікно, щоб юзер встиг оновити картку;
 * якщо ретраї вичерпані — прилетить subscription.canceled і plan стане free.
 */
const PAST_DUE_GRACE_MS = 7 * 24 * 60 * 60 * 1000;

function graceMsFor(status: SubscriptionStatus | null): number {
  if (status === "active" || status === "trialing") return ACTIVE_RENEWAL_GRACE_MS;
  if (status === "past_due") return PAST_DUE_GRACE_MS;
  // scheduled_cancel / canceled / paused / NULL (legacy): ріжемо чітко по даті —
  // для скасованої підписки продовжувати доступ понад оплачений період не можна.
  return 0;
}

/**
 * ЄДИНЕ місце деривації "ефективного" плану з рядка user_profiles.
 * Використовується сервером (getUserPlan/getServerAuth) і клієнтом (useUser),
 * щоб логіка (expiry + grace) не розїжджалась між середовищами.
 */
export function resolveEffectivePlan(
  profile: ProfileSubscriptionRow | null | undefined,
  now: Date = new Date(),
): EffectivePlanState {
  if (!profile) {
    return {
      plan: "free",
      planExpiresAt: null,
      subscriptionStatus: null,
      isLapsedPro: false,
    };
  }

  const status = isSubscriptionStatus(profile.subscription_status)
    ? profile.subscription_status
    : null;
  const planExpiresAt = profile.plan_expires_at;

  if (!profile.plan || !PAID_PLANS.has(profile.plan)) {
    return { plan: "free", planExpiresAt, subscriptionStatus: status, isLapsedPro: false };
  }
  const paidPlan = profile.plan as "pro" | "studio";

  // plan_expires_at NULL для платного = "вічний" (захищений синтетичною датою
  // у вебхуку; NULL можливий лише вручну) — не ріжемо.
  const expiresMs = planExpiresAt ? new Date(planExpiresAt).getTime() : null;
  const lapsed =
    expiresMs !== null && now.getTime() > expiresMs + graceMsFor(status);

  return {
    plan: lapsed ? "free" : paidPlan,
    planExpiresAt,
    subscriptionStatus: status,
    isLapsedPro: lapsed,
  };
}
